import isEmpty from 'lodash/isEmpty';
import noop from 'lodash/noop';
import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Button as BootstrapButton, Overlay, Tooltip } from 'react-bootstrap';
import { ButtonVariant } from 'react-bootstrap/esm/types';
import { PulseLoader } from 'react-spinners';

import useBreakpoint from '../../hooks/useBreakpoint';
import Icon from '../icon/Icon';
import { useMergeRefs } from '@floating-ui/react';
import { Placement } from 'react-bootstrap/esm/Overlay';

const variantSpinnerColorMap = (variant: ButtonVariant) => {
  switch (variant) {
    case 'outline-primary':
      return '#027959';
    case 'disabled':
      return '#dcdcdc';
    default:
      return '#fff';
  }
};

export interface ButtonProps extends React.ComponentProps<'button'> {
  variant?: ButtonVariant;
  loading?: boolean;
  disabled?: boolean;
  checked?: boolean;
  icon?: string;
  tooltipProps?: {
    placement?: Placement;
    text?: string;
  };
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(({
  children,
  loading,
  disabled,
  icon,
  tooltipProps,
  variant = 'primary',
  onClick,
  ...rest
}, ref) => {
  const target = useRef<HTMLButtonElement>(null);
  const [isTooltipVisible, setIsTooltipVisible] = useState(false);
  const isEnabledForBreakpoint = useBreakpoint(['md', 'lg', 'xl']);
  const enableTooltip = !isEmpty(tooltipProps) && isEnabledForBreakpoint;

  useEffect(() => {
    if (rest.autoFocus && target.current) {
      // inevitable setTimeout on focus like implmentation in mapView.js --> focusSiteAddress();
      setTimeout(() => target.current?.focus(), 100);
    }
  }, [target.current, rest.autoFocus]);

  // const mergedRef = useMergeRefs([ref, target]);
  useImperativeHandle(ref, () => target.current as HTMLButtonElement, []);

  return (
    <>
      <BootstrapButton
        ref={target}
        onBlur={() => enableTooltip && setIsTooltipVisible(false)}
        onMouseEnter={() => enableTooltip && setIsTooltipVisible(true)}
        onMouseLeave={() => enableTooltip && setIsTooltipVisible(false)}
        disabled={disabled}
        variant={variant}
        onClick={loading ? noop : onClick}
        {...rest}
      >
        {loading ? (
          <PulseLoader
            color={variantSpinnerColorMap(!disabled ? variant : 'disabled')}
            size={12}
          />
        ) : (
          <>
            {icon && <Icon icon={icon} />}
            {children}
          </>
        )}
      </BootstrapButton>
      {enableTooltip && (
        <Overlay
          target={target.current}
          show={isTooltipVisible}
          placement={tooltipProps.placement}
        >
          {(props) => (
            <Tooltip id='button-tooltip' {...props}>
              {tooltipProps.text}
            </Tooltip>
          )}
        </Overlay>
      )}
    </>
  );
})
Button.displayName = 'Button';

export default Button;
